import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Stepper,
    Step,
    StepLabel,
    Box,
    Typography,
    Alert,
    CircularProgress,
    LinearProgress,
} from '@mui/material';
import { devicesApi } from '@api/devices';

interface CalibrationDialogProps {
    open: boolean;
    onClose: () => void;
    deviceId: number;
    deviceName: string;
}

const steps = ['Tare (Zero)', 'Place Weight', 'Complete'];

const CalibrationDialog: React.FC<CalibrationDialogProps> = ({
    open,
    onClose,
    deviceId,
    deviceName,
}) => {
    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [knownWeight, setKnownWeight] = useState('');
    const [currentReading, setCurrentReading] = useState(0);
    const [error, setError] = useState('');

    const handleTare = async () => {
        setLoading(true);
        setError('');

        try {
            await devicesApi.calibrateDevice(deviceId, { action: 'tare' });
            setActiveStep(1);
        } catch (err) {
            setError('Failed to tare scale. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleCalibrate = async () => {
        if (!knownWeight || parseFloat(knownWeight) <= 0) {
            setError('Please enter a valid weight greater than 0');
            return;
        }

        setLoading(true);
        setError('');

        try {
            await devicesApi.calibrateDevice(deviceId, {
                action: 'calibrate',
                known_weight_grams: parseFloat(knownWeight) * 1000, // Convert kg to grams
            });
            setActiveStep(2);
        } catch (err) {
            setError('Calibration failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setActiveStep(0);
        setKnownWeight('');
        setError('');
        setCurrentReading(0);
    };

    const renderStepContent = () => {
        switch (activeStep) {
            case 0:
                return (
                    <Box>
                        <Alert severity="info" sx={{ mb: 3 }}>
                            Remove all weight from the scale, including the hive. The scale should be completely empty.
                        </Alert>
                        <Typography variant="body1" gutterBottom>
                            Step 1: Establish Zero Point (Tare)
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                            This removes the weight of the platform and any permanent fixtures.
                        </Typography>
                        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                    </Box>
                );

            case 1:
                return (
                    <Box>
                        <Alert severity="warning" sx={{ mb: 3 }}>
                            Place a known weight on the scale. Use a calibrated weight or an object with known mass.
                        </Alert>
                        <Typography variant="body1" gutterBottom>
                            Step 2: Calibrate with Known Weight
                        </Typography>

                        <TextField
                            fullWidth
                            label="Known Weight (kg)"
                            type="number"
                            value={knownWeight}
                            onChange={(e) => setKnownWeight(e.target.value)}
                            placeholder="e.g., 10"
                            helperText="Enter the exact weight of the calibration object in kilograms"
                            sx={{ mt: 2, mb: 2 }}
                            inputProps={{
                                step: 0.1,
                                min: 0.1,
                                max: 150,
                            }}
                        />

                        <Typography variant="caption" color="text.secondary">
                            Common calibration weights: 5kg, 10kg, 20kg. You can also use filled water bottles
                            (1 liter = 1 kg).
                        </Typography>

                        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                    </Box>
                );

            case 2:
                return (
                    <Box textAlign="center">
                        <Alert severity="success" sx={{ mb: 3 }}>
                            ✅ Calibration Complete!
                        </Alert>
                        <Typography variant="body1" gutterBottom>
                            Your scale is now calibrated and ready to use.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                            The scale will now accurately measure weights up to its maximum capacity.
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Recommended: Verify calibration by weighing a known object.
                        </Typography>
                    </Box>
                );

            default:
                return null;
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Calibrate Weight Sensor - {deviceName}
            </DialogTitle>

            <DialogContent>
                <Box sx={{ pt: 2 }}>
                    <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>

                    {loading && <LinearProgress sx={{ mb: 2 }} />}

                    {renderStepContent()}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
                {activeStep === 2 ? (
                    <>
                        <Button onClick={handleReset}>Calibrate Again</Button>
                        <Button onClick={onClose} variant="contained">
                            Done
                        </Button>
                    </>
                ) : (
                    <>
                        <Button onClick={onClose} disabled={loading}>
                            Cancel
                        </Button>
                        {activeStep === 0 && (
                            <Button
                                onClick={handleTare}
                                variant="contained"
                                disabled={loading}
                            >
                                {loading ? <CircularProgress size={20} /> : 'Tare Scale'}
                            </Button>
                        )}
                        {activeStep === 1 && (
                            <Button
                                onClick={handleCalibrate}
                                variant="contained"
                                disabled={loading || !knownWeight}
                            >
                                {loading ? <CircularProgress size={20} /> : 'Calibrate'}
                            </Button>
                        )}
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default CalibrationDialog;
